/**
 * One readable sentence from a failed API response.
 *
 * FastAPI answers an error in two shapes: our own `{detail: "…"}` (a plain
 * sentence written for the person, e.g. why Amazon links can't be forwarded —
 * shown verbatim), and pydantic's 422 `{detail: [{loc, msg, type}]}`, which is
 * a list of machine-shaped complaints. The list is turned into short
 * "field: message" lines so a form can show it without dumping JSON.
 *
 * `ApiError` keeps the HTTP status so a page can tell "this link isn't active"
 * (404) from "the network hiccupped".
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const words = (s: string) => s.replace(/_/g, ' ');

export function detailMessage(body: unknown, status: number): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const lines = detail
      .map((item) => {
        const it = item as { loc?: unknown; msg?: unknown };
        const msg = typeof it?.msg === 'string' ? it.msg.replace(/^Value error,\s*/i, '') : '';
        if (!msg) return '';
        const loc = Array.isArray(it.loc)
          ? it.loc.filter((p): p is string => typeof p === 'string' && p !== 'body' && p !== 'query' && p !== 'path')
          : [];
        return loc.length ? `${words(loc[loc.length - 1])}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (lines.length) return lines.join('; ');
  }
  if (status === 429) return 'Too many tries in a short time — wait a little and try again.';
  return `Request failed (${status})`;
}

/** Read a failed response's body (JSON or not) into an ApiError. */
export async function errorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error body — the status line is all there is */
  }
  return new ApiError(detailMessage(body, res.status), res.status);
}
